import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { APP_API_URL } from '../utils';
import { useNavigate } from 'react-router-dom';
import DuoModal from './ui/duo-modal';
import formatNumber from '../utils/functions';

export default function ProsFormatList({ refreshOrder, onEdit }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/pros-format`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sorted = (response.data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setOrders(sorted);
      setFilteredOrders(sorted);
    } catch {
      setError('Erreur lors du chargement des commandes Pro Format.');
      setOrders([]);
      setFilteredOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchOrders();
  }, [refreshOrder, open]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOrders(orders);
      return;
    }
    const lowerSearch = searchTerm.toLowerCase();
    setFilteredOrders(
      orders.filter(
        (order) =>
          order.reference?.toLowerCase().includes(lowerSearch) ||
          order.agent_nom?.toLowerCase().includes(lowerSearch)
      )
    );
  }, [searchTerm, orders]);

  const handleDelete = async (orderId) => {
    const result = await Swal.fire({
      title: 'Êtes-vous sûr ?',
      text: 'Cette action supprimera définitivement la commande Pro Format.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${APP_API_URL}/pros-format/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        Swal.fire('Supprimé', 'La commande a été supprimée.', 'success');
        fetchOrders();
      } catch {
        Swal.fire('Erreur', 'Impossible de supprimer la commande.', 'error');
      }
    }
  };

  const openDetails = (reference) => {
    setOpen(false);
    navigate('/commandes/pros-format', { state: { reference } });
  };

  return (
    <>
      <button type="button" className="duo-btn duo-btn--ghost" onClick={() => setOpen(true)}>
        <i className="bi bi-cart" /> Pro Format
      </button>

      {open && (
        <DuoModal
          title="Commandes Pro Format"
          onClose={() => setOpen(false)}
          size="xl"
          footer={
            <button type="button" className="duo-btn duo-btn--ghost" onClick={() => setOpen(false)}>
              Fermer
            </button>
          }
        >
          <input
            type="search"
            className="duo-input mb-3"
            placeholder="Rechercher par référence ou agent..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {loading && <p className="duo-empty mb-0">Chargement...</p>}
          {error && <p className="text-danger mb-0">{error}</p>}
          {!loading && !error && filteredOrders.length === 0 && (
            <p className="duo-empty mb-0">Aucune commande Pro Format trouvée.</p>
          )}

          {!loading && !error && filteredOrders.length > 0 && (
            <div className="table-responsive">
              <table className="duo-table">
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Agent</th>
                    <th className="text-end">Montant net</th>
                    <th>Date</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.id}>
                      <td>{order.reference}</td>
                      <td>{order.agent_nom}</td>
                      <td className="text-end">{formatNumber(order.montant_net)} FCFA</td>
                      <td>{new Date(order.created_at).toLocaleDateString('fr-FR')}</td>
                      <td className="text-end">
                        <div className="d-flex flex-wrap gap-2 justify-content-end">
                          <button type="button" className="duo-btn duo-btn--primary duo-btn--sm" onClick={() => openDetails(order.reference)}>
                            Voir
                          </button>
                          {onEdit && (
                            <button
                              type="button"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              onClick={async () => {
                                setOpen(false);
                                await onEdit(order);
                              }}
                            >
                              Modifier
                            </button>
                          )}
                          <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm" style={{ color: '#ff4b4b', borderColor: '#ff4b4b' }} onClick={() => handleDelete(order.id)}>
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DuoModal>
      )}
    </>
  );
}
